import * as Sentry from '@sentry/browser';
import { GameUpdate } from '@colonist/game-state-types';
import { GameClientState } from '../../../../shared/GameClientState.ts';
import { CA } from '../analytics/Analytics.ts';
import { sendRushInitialPlacementTimeoutAnalytic } from '../analytics/sendRushInitialPlacementTimeoutAnalytic.ts';
import { ViewPopupTemporary } from '../ui_popup/ViewPopupTemporary.ts';
import { notificationController, socket, uiGameManager } from '../EntryShared.ts';
import { UITopNotificationType } from '../notifications/UINotificationController.ts';
import { ModeSettingEnum, SOCKET_CLOSE_GAME_CLIENT_ERROR } from '@colonist/types';
import { createDeferredPromise, SequencedDataReceiver, TimeUtils } from '@colonist/utils';
import { StorageWrapper } from '../StorageWrapper.ts';
import { GameStateEnums, GameTypeEnum, NetworkEnums } from '../../../../shared/GameEnums.ts';
import { UIReconnectionController } from '../ui_reconnection/UIReconnectionController.ts';
import { addGlobalSpectatorMessage, setGlobalCanResign, setGlobalSpectators } from '@colonist/ui-game';
import { SocketLobbySend } from './SocketLobbySend.ts';
import { AnalyticEventCategory, AnalyticEventType } from '../../../../shared/entity_datas/EntityDatas.ts';
import { omit, omitBy } from 'lodash-es';
import { AudioManager, AudioType } from '@colonist/audio';
const GAME_STARTED_DELAY_FOR_NATURAL_EFFECT = 500;
export class SocketGameListen {
    restoreGameClientWithoutClosingSocket() {
        var _this_uiGameManager;
        (_this_uiGameManager = this.uiGameManager) === null || _this_uiGameManager === void 0 ? void 0 : _this_uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.ClientReconnectionTriggered);
        this.reset();
        this.gameClientRestorationController.startReconnecting();
    }
    reset() {
        var _this_gameStateReceiver;
        (_this_gameStateReceiver = this.gameStateReceiver) === null || _this_gameStateReceiver === void 0 ? void 0 : _this_gameStateReceiver.reset();
    }
    static playGameStartedSound(mode) {
        const getGameStartedSoundForMode = (modeSetting)=>{
            switch(modeSetting){
                case ModeSettingEnum.ColonistRush4P:
                    return AudioType.GameStartedGuitar;
                default:
                    return AudioType.GameStarted;
            }
        };
        const gameStartedSound = getGameStartedSoundForMode(mode);
        setTimeout(()=>AudioManager.play(gameStartedSound), GAME_STARTED_DELAY_FOR_NATURAL_EFFECT);
    }
    static playSettlementPhaseEndedSound(mode) {
        const getSettlementPhaseEndedSoundForMode = (modeSetting)=>{
            switch(modeSetting){
                case ModeSettingEnum.ColonistRush4P:
                    return AudioType.SettlementPhaseEndedGuitar;
                default:
                    return AudioType.SettlementPhaseEnded;
            }
        };
        const settlementPhaseEndedSound = getSettlementPhaseEndedSoundForMode(mode);
        setTimeout(()=>AudioManager.play(settlementPhaseEndedSound), GAME_STARTED_DELAY_FOR_NATURAL_EFFECT);
    }
    constructor(socketController, socketActionController){
        this.gameStateHandlers = new Map();
        this.playedStartGameSound = false;
        this.gameState = new GameClientState();
        this.socketActionController = socketActionController;
        this.uiGameManager = uiGameManager;
        const onGameReconnectionAttempt = ()=>{
            var _this_socketActionController_uiGameManager;
            const reconnectPromise = createDeferredPromise();
            this.currentReconnectAttempt = reconnectPromise;
            setTimeout(()=>{
                if (!reconnectPromise.pending) return;
                reconnectPromise.resolve('retry');
            }, 10 * TimeUtils.MILLISECONDS);
            (_this_socketActionController_uiGameManager = this.socketActionController.uiGameManager) === null || _this_socketActionController_uiGameManager === void 0 ? void 0 : _this_socketActionController_uiGameManager.socketGameSend.requestGameState();
            return reconnectPromise;
        };
        this.gameClientRestorationController = new UIReconnectionController(onGameReconnectionAttempt, ()=>true);
        this.gameStateReceiver = new SequencedDataReceiver((data)=>{
            try {
                var _this_gameStateHandlers_get;
                (_this_gameStateHandlers_get = this.gameStateHandlers.get(data.type)) === null || _this_gameStateHandlers_get === void 0 ? void 0 : _this_gameStateHandlers_get(data.payload);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
                socketController.close(SOCKET_CLOSE_GAME_CLIENT_ERROR);
                Sentry.captureException(e);
            }
        }, (lastSequence, now, errorReason, pendingData, lastReleasedData)=>{
            void CA.sendAnalyticEvent({
                category: AnalyticEventCategory.Socket,
                type: AnalyticEventType.SocketSequenceError,
                dimensions: {
                    databaseGameId: this.databaseGameId,
                    lastSequence: lastSequence,
                    time: now,
                    reason: errorReason,
                    pendingData: pendingData.map((entry)=>({
                            t: entry.added,
                            s: entry.data.sequence,
                            gu: entry.data.type
                        })),
                    lastReleasedData: lastReleasedData.map((entry)=>({
                            t: entry.added,
                            s: entry.data.sequence,
                            gu: entry.data.type
                        })),
                    monitorStats: omitBy(omit(socket.monitor.stats, [
                        'buckets'
                    ]), (value)=>value == 0)
                }
            });
            this.restoreGameClientWithoutClosingSocket();
        });
        socketController.setStateHandler(NetworkEnums.State.GameStateUpdate, (data)=>{
            this.gameStateReceiver.addData(data);
        });
        socketController.registerSessionEstablishedObserver(()=>{
            this.reset();
            if (this.gameSettingId == undefined) return;
            SocketLobbySend.reconnectGame(this.gameSettingId);
            this.gameClientRestorationController.startReconnecting();
        });
        this.gameStateHandlers.set(GameUpdate.FirstGameState, (data)=>{
            if (this.socketActionController.uiGameManager == undefined) return;
            socket.monitor.activate();
            // ======================
            // Order maters, these variables have to be set before executing other logic
            this.gameState.serverId = data.serverId;
            this.databaseGameId = data.databaseGameId;
            this.gameSettingId = data.gameSettingId;
            // ======================
            if (data.shouldResetGameClient) {
                var _this_currentReconnectAttempt;
                (_this_currentReconnectAttempt = this.currentReconnectAttempt) === null || _this_currentReconnectAttempt === void 0 ? void 0 : _this_currentReconnectAttempt.resolve('connected');
                this.socketActionController.uiGameManager.resetGameUIToRebuildOnReconnect();
            }
            notificationController === null || notificationController === void 0 ? void 0 : notificationController.removeDisconnectBanners();
            this.socketActionController.onConnectionEstablished(data);
            if (data.isReconnectingSession) this.playedStartGameSound = true;
        });
        this.gameStateHandlers.set(GameUpdate.SpectatorData, (data)=>{
            setGlobalSpectators(data);
        });
        this.gameStateHandlers.set(GameUpdate.BeginnerHintActivated, (data)=>{
            var _this_uiGameManager;
            this.socketActionController.onBeginnerHintActivated(data);
            (_this_uiGameManager = this.uiGameManager) === null || _this_uiGameManager === void 0 ? void 0 : _this_uiGameManager.gameStore.getState().gameBeginnerHint.setBeginnerHintData(data);
        });
        this.gameStateHandlers.set(GameUpdate.BeginnerModeActionUpdate, (data)=>{
            var _this_uiGameManager;
            this.socketActionController.onBeginnerModeActionUpdate(data);
            (_this_uiGameManager = this.uiGameManager) === null || _this_uiGameManager === void 0 ? void 0 : _this_uiGameManager.gameStore.getState().gameBeginnerHint.setBeginnerActionState(data);
        });
        this.gameStateHandlers.set(GameUpdate.BeginnerModeLevelEnd, (data)=>{
            this.socketActionController.onBeginnerModeLevelEnd();
        });
        this.gameStateHandlers.set(GameUpdate.Vote, (data)=>{
            var _this_uiGameManager;
            const voteState = data.done ? undefined : data;
            (_this_uiGameManager = this.uiGameManager) === null || _this_uiGameManager === void 0 ? void 0 : _this_uiGameManager.gameStore.getState().gameVote.setVoteState(voteState);
        });
        this.gameStateHandlers.set(GameUpdate.SelectPlayer, (data)=>{
            this.socketActionController.onSelectPlayer(data);
        });
        this.gameStateHandlers.set(GameUpdate.ExchangeCards, (data)=>{
            this.socketActionController.onExchangeCards(data);
        });
        this.gameStateHandlers.set(GameUpdate.HighlightCorners, (data)=>{
            this.socketActionController.onHighlightCorners(data);
        });
        this.gameStateHandlers.set(GameUpdate.HighlightRoadEdges, (data)=>{
            this.socketActionController.onHighlightRoadEdges(data);
        });
        this.gameStateHandlers.set(GameUpdate.HighlightShipEdges, (data)=>{
            this.socketActionController.onHighlightShipEdges(data);
        });
        this.gameStateHandlers.set(GameUpdate.HighlightTiles, (data)=>{
            this.socketActionController.onHighlightTiles(data);
        });
        this.gameStateHandlers.set(GameUpdate.HighlightDiceNumbersForInventor, (data)=>{
            this.socketActionController.onHighlightDiceNumbersForInventor(data);
        });
        this.gameStateHandlers.set(GameUpdate.AmountOfCardsToDiscard, (data)=>{
            this.socketActionController.onAmountOfCardsToDiscard(data);
            AudioManager.play(AudioType.DiscardNotification);
        });
        this.gameStateHandlers.set(GameUpdate.DiscardBroadcast, ()=>{
            AudioManager.play(AudioType.DiscardBroadcast);
        });
        this.gameStateHandlers.set(GameUpdate.SelectCardsToGiveToPlayer, (data)=>{
            this.socketActionController.onSelectCardsToGiveToPlayer(data);
            AudioManager.play(AudioType.YourTurn);
        });
        this.gameStateHandlers.set(GameUpdate.CardsToSelectForDevelopmentCard, (data)=>{
            this.socketActionController.onCardsToSelectForDevelopmentCard(data);
        });
        this.gameStateHandlers.set(GameUpdate.SelectPlayerAndCard, (data)=>{
            this.socketActionController.onSelectPlayerAndCard(data);
        });
        this.gameStateHandlers.set(GameUpdate.CardsToSelectForPendingTransaction, (data)=>{
            this.socketActionController.onCardsToSelectForPendingTransaction(data);
        });
        this.gameStateHandlers.set(GameUpdate.ConfirmUseDevelopmentCard, (data)=>{
            this.socketActionController.onConfirmUseDevelopmentCard(data);
        });
        this.gameStateHandlers.set(GameUpdate.SelectPlayerUsingDevelopmentCard, (data)=>{
            this.socketActionController.onSelectPlayerUsingDevelopmentCard(data);
        });
        this.gameStateHandlers.set(GameUpdate.StealDevelopmentCardFromPlayer, (data)=>{
            this.socketActionController.onStealDevelopmentCardFromPlayer(data);
        });
        this.gameStateHandlers.set(GameUpdate.StealResourceCardFromPlayer, (data)=>{
            this.socketActionController.onStealResourceCardFromPlayer(data);
        });
        this.gameStateHandlers.set(GameUpdate.ClosePopupUI, ()=>{
            this.socketActionController.onClosePopupUI();
        });
        this.gameStateHandlers.set(GameUpdate.SelectDice, (data)=>{
            this.socketActionController.onSelectDice(data);
        });
        this.gameStateHandlers.set(GameUpdate.RushInitialPlacementTimedOutAnalytic, (data)=>{
            sendRushInitialPlacementTimeoutAnalytic(this.databaseGameId, data);
        });
        this.gameStateHandlers.set(GameUpdate.CityImprovementStateUpdated, ()=>{
            this.socketActionController.onCityImprovementStateUpdated();
        });
        this.gameStateHandlers.set(GameUpdate.ForceShowCityImprovementButtons, ()=>{
            this.socketActionController.onForceShowCityImprovementButtons();
        });
        this.gameStateHandlers.set(GameUpdate.PlayTurnSound, ()=>{
            AudioManager.play(AudioType.YourTurn);
        });
        this.gameStateHandlers.set(GameUpdate.InfoMessage, (data)=>{
            const popup = new ViewPopupTemporary({
                key: 'strings:popups.informationView.title'
            }, data);
        });
        this.gameStateHandlers.set(GameUpdate.ResetTradeStateAtEndOfTurn, (data)=>{
            this.socketActionController.onResetTradeStateAtEndOfTurn();
        });
        this.gameStateHandlers.set(GameUpdate.BoldTextInTradeWindow, (cardsToBold)=>{
            this.socketActionController.onBoldTextInTradeWindow(cardsToBold);
        });
        this.gameStateHandlers.set(GameUpdate.BuildGame, (data)=>{
            this.socketActionController.onBuildGame(data);
            if (!this.playedStartGameSound) {
                SocketGameListen.playGameStartedSound(data.gameSettings.modeSetting);
                this.playedStartGameSound = true;
            }
        });
        this.gameStateHandlers.set(GameUpdate.GameStateUpdated, (timedGameStateUpdate)=>{
            this.socketActionController.gameStateUpdated(timedGameStateUpdate, true);
        });
        this.gameStateHandlers.set(GameUpdate.GivePlayerResourcesFromTile, (data)=>{
            this.socketActionController.onGivePlayerResourcesFromTile(data);
        });
        this.gameStateHandlers.set(GameUpdate.RematchLink, (data)=>{
            window.location.hash = data;
            window.location.reload();
        });
        this.gameStateHandlers.set(GameUpdate.EndGameText, (data)=>{
            this.socketActionController.onEndGameText(data);
        });
        this.gameStateHandlers.set(GameUpdate.TogglePauseGame, (data)=>{
            this.socketActionController.onTogglePauseGame(data);
        });
        this.gameStateHandlers.set(GameUpdate.CanResignGame, (canResign)=>{
            this.gameState.canResign = canResign;
            setGlobalCanResign(canResign);
        });
        this.gameStateHandlers.set(GameUpdate.GameEndState, (gameEndData)=>{
            this.uiGameManager.socketGameSend.endBeginnerModeLevel();
            socketActionController.onGameEndState(gameEndData);
            socketController.reconnectionController.disable();
            this.gameClientRestorationController.disable();
            if (this.uiGameManager.gameController.gameSettings.modeSetting == ModeSettingEnum.ColonistRush4P) {
                StorageWrapper.incrementRushGamesPlayed();
                const gameType = this.uiGameManager.gameController.gameSettings.gameType;
                if (gameType == GameTypeEnum.BotGame) StorageWrapper.incrementRushBotGameCompletionCount();
            }
            CA.sendGoogleTagManagerEvent(CA.GoogleTagManagerEvent.GameFinished);
        });
        this.gameStateHandlers.set(GameUpdate.ExitInitialPlacement, ()=>{
            SocketGameListen.playSettlementPhaseEndedSound(this.uiGameManager.gameController.gameSettings.modeSetting);
        });
        this.gameStateHandlers.set(GameUpdate.TradeFinalized, ()=>{
            AudioManager.play(AudioType.OfferAccepted);
        });
        this.gameStateHandlers.set(GameUpdate.TradeRejectedByAll, ()=>{
            AudioManager.play(AudioType.OfferRejected);
        });
        this.gameStateHandlers.set(GameUpdate.KnightPlaced, ()=>{
            AudioManager.play(AudioType.KnightPlace);
        });
        this.gameStateHandlers.set(GameUpdate.KarmaState, (data)=>{
            this.uiGameManager.gameController.gameSettings.karmaActive = data.isActive;
            this.uiGameManager.gameStore.getState().gameSettings.setIsKarmaActive(data.isActive);
            this.uiGameManager.gameStore.getState().setHasUsedDisableKarmaRequest(data.hasUsedDisableRequest);
        });
        this.gameStateHandlers.set(GameUpdate.KnightActivated, ()=>{
            AudioManager.play(AudioType.KnightEquip);
        });
        this.gameStateHandlers.set(GameUpdate.KnightUpgraded, ()=>{
            AudioManager.play(AudioType.KnightUpgrade);
        });
        this.gameStateHandlers.set(GameUpdate.PlayerReconnected, ()=>{
            AudioManager.play(AudioType.Reconnect);
        });
        this.gameStateHandlers.set(GameUpdate.PlayerDisconnected, ()=>{
            AudioManager.play(AudioType.Disconnect);
        });
        this.gameStateHandlers.set(GameUpdate.SpectatorChatMessage, (data)=>{
            addGlobalSpectatorMessage(data);
        });
        this.gameStateHandlers.set(GameUpdate.ResetBeginnerModeLevel, ()=>{
            StorageWrapper.resetBeginnerModeLevel();
        });
        this.gameStateHandlers.set(GameUpdate.SendBroadcast, (data)=>{
            notificationController.show(data.text, UITopNotificationType.Information);
        });
        this.gameStateHandlers.set(GameUpdate.Ping, ()=>{
            var _this_socketActionController_uiGameManager;
            (_this_socketActionController_uiGameManager = this.socketActionController.uiGameManager) === null || _this_socketActionController_uiGameManager === void 0 ? void 0 : _this_socketActionController_uiGameManager.socketGameSend.pong();
        });
        this.gameStateHandlers.set(GameUpdate.TriggerClientGameStateReset, ()=>{
            this.restoreGameClientWithoutClosingSocket();
        });
    }
}
